import { activeProviderUuid } from './screenTracking'
import { currentBehaviorSessionId } from './session'
import type {
  BehaviorAction,
  BehaviorEventName,
  BehaviorEventPayload,
  BehaviorMetadataByAction,
  BehaviorScreen,
} from './types'
import { uuid } from './uuid'

/**
 * The in-app behavior instrumentation primitive (VH-2669).
 *
 * `trackEvent()` buffers; the buffer flushes on a timer, at a size threshold,
 * and when the page goes away. Nothing here is allowed to throw into the UI or
 * to make a user wait — a dropped event is always preferable to a broken page.
 *
 * ## Why this does not use the shared axios instance
 *
 * `@axios` is the right client for product requests and the wrong one for
 * telemetry, in four separate ways:
 *
 * 1. **Route-change cancellation.** `plugins/axios.ts` cancels every pending
 *    request on navigation. `screen_viewed` fires *on navigation*, so the
 *    shared client would cancel the request carrying it — the most important
 *    event in the epic, lost every time.
 * 2. **A 429 shows a toast.** The endpoint is throttled, so an over-eager
 *    client would put "Too many requests" in front of a user because of
 *    telemetry.
 * 3. **A 401 calls `auth.logout()`.** A background flush racing a session
 *    expiry should not be the thing that decides to sign someone out.
 * 4. **Every other error goes to Rollbar.** A telemetry outage would bury real
 *    errors under thousands of reports.
 *
 * Each has an opt-out flag, but needing four of them is the signal: telemetry
 * should share no interceptors with the product, precisely so that "this
 * cannot break the app" is structural rather than a list of flags someone
 * has to remember on the next call.
 *
 * ## Why `fetch(..., { keepalive: true })` and not `sendBeacon`
 *
 * `sendBeacon` cannot set request headers, and these routes sit behind
 * Sanctum's stateful CSRF check — a beacon would be rejected with a 419 every
 * time. `keepalive` gives the same survives-page-unload guarantee while still
 * allowing `X-XSRF-TOKEN`, so it is used for the ordinary flush too and there
 * is only one transport path to reason about.
 */

const ENDPOINT = `${import.meta.env.VITE_SERVER_URL}/api/behavior-events`

/** Flush cadence while a tab is open and idle-ish. */
const FLUSH_INTERVAL_MS = 15_000

/** Flush as soon as this many events are waiting. */
const FLUSH_THRESHOLD = 20

/** The server rejects a batch larger than this. */
const MAX_BATCH = 100

/**
 * Hard cap on unflushed events. Reached only when the endpoint is unreachable;
 * without it a long outage with a tab open grows the buffer without bound.
 * Oldest go first — a half-captured funnel from ten minutes ago is worth less
 * than what the user is doing right now.
 */
const MAX_BUFFER = 200

/**
 * An event plus the provider it was recorded under.
 *
 * The provider travels with the event rather than being read at flush time.
 * It is sent as a header, and a sub-threshold event sitting in the buffer
 * outlives the screen that produced it — so resolving it later attributes a
 * hospital's activity to whichever hospital the user happened to navigate to
 * before the timer fired, and lets one request carry events from two
 * hospitals under a single header.
 */
interface BufferedEvent {
  payload: BehaviorEventPayload
  providerUuid: string | null
}

let buffer: BufferedEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let listenersAttached = false

/**
 * The provider each attempt's first event was recorded under (VH-2811).
 *
 * An attempt can outlive the page it started on: a result is abandoned as the
 * workspace unmounts, a verification answers after the user has navigated, a
 * paused recovery is dropped from wherever the next one starts. By then the
 * router has moved the current provider, so reading it at call time filed
 * those events under the destination — another hospital, or none, which gets
 * an admin's whole batch rejected (Copilot). Pinned at the first event
 * instead, so every event of an attempt goes under the hospital it began in.
 *
 * Bounded, oldest out. An attempt whose pin was evicted falls back to the
 * current provider, which is wrong only if it emits again after this many
 * newer attempts have started.
 */
const attemptProviders = new Map<string, string | null>()

const MAX_PINNED_ATTEMPTS = 500

function providerFor(attemptId: string | undefined): string | null {
  if (!attemptId)
    return activeProviderUuid()

  if (attemptProviders.has(attemptId))
    return attemptProviders.get(attemptId) ?? null

  const providerUuid = activeProviderUuid()

  attemptProviders.set(attemptId, providerUuid)

  if (attemptProviders.size > MAX_PINNED_ATTEMPTS) {
    const oldest = attemptProviders.keys().next().value

    if (oldest !== undefined)
      attemptProviders.delete(oldest)
  }

  return providerUuid
}

/**
 * A new funnel attempt's id (VH-2811).
 *
 * Mint one when an attempt starts and pass the same value as `attemptId` on
 * every event of that attempt: its start, each step, and how it ended. That
 * is what lets the server pair a start with its outcome. Without it a funnel
 * can only count events, and a completion rate is out of reach.
 */
export function newAttemptId(): string {
  return uuid()
}

/** Laravel's CSRF cookie, as axios would read it. */
export function xsrfToken(): string | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)

    return match ? decodeURIComponent(match[1]) : null
  } catch {
    return null
  }
}

/**
 * ISO 8601 with milliseconds.
 *
 * `toISOString()` already keeps them; the server's columns are
 * millisecond-precision for the same reason, since a funnel step that starts
 * and finishes inside one second would otherwise measure as instant.
 */
function timestamp(): string {
  return new Date().toISOString()
}

function scheduleFlush(): void {
  if (flushTimer !== null)
    return

  flushTimer = setTimeout(() => {
    flushTimer = null
    flushBehaviorEvents()
  }, FLUSH_INTERVAL_MS)
}

/**
 * Send what is buffered.
 *
 * Events are taken out of the buffer before the request and are **not put
 * back on failure**. Re-queueing would turn a server outage into an
 * ever-growing buffer and a retry storm, and the data is not worth either.
 */
export async function flushBehaviorEvents(): Promise<void> {
  if (buffer.length === 0)
    return

  // One request carries one provider. Take the oldest event's provider and
  // everything else queued under it, in order, leaving the rest for the next
  // flush rather than dropping or mislabelling it.
  const targetProvider = buffer[0].providerUuid
  const batch: BufferedEvent[] = []
  const remainder: BufferedEvent[] = []

  for (const entry of buffer) {
    if (batch.length < MAX_BATCH && entry.providerUuid === targetProvider)
      batch.push(entry)
    else
      remainder.push(entry)
  }

  buffer = remainder

  // Anything still waiting gets its own flush rather than waiting a full
  // interval — a burst larger than one batch should drain, not trickle.
  if (buffer.length > 0)
    scheduleFlush()

  const token = xsrfToken()

  // A Vector admin has no single "current" provider server-side: which one
  // they are acting as comes from this header, and plugins/axios.ts sets it
  // on every normal request from the same route param. Telemetry does not go
  // through axios, so it has to carry the header itself — without it the
  // endpoint cannot resolve a provider for an admin and rejects the whole
  // batch with a 403. Found end to end; neither side's unit tests could see
  // it, because the server tests set the header by hand and the client tests
  // never reach a server.
  //
  // It is a hospital identifier, not patient data, and it is already in the
  // URL of the page being measured. The server still derives provider_id
  // itself and never reads a provider from the body.
  const providerUuid = targetProvider

  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { 'X-XSRF-TOKEN': token } : {}),
        ...(providerUuid ? { 'X-Provider-UUID': providerUuid } : {}),
      },
      body: JSON.stringify({ events: batch.map(entry => entry.payload) }),
    })
  } catch {
    // Swallowed deliberately. A network failure, an offline tab, or a
    // keepalive request cut short by the page unloading are all expected, and
    // none of them is a reason to surface anything to the user or to Rollbar.
  }
}

/**
 * Flush on the way out.
 *
 * `visibilitychange` is the one that actually fires reliably on mobile and on
 * tab close; `pagehide` covers bfcache navigations. Both are registered
 * because neither alone catches every exit, and a duplicate flush is harmless
 * — an empty buffer is a no-op, and the server dedupes on `client_event_id`
 * anyway.
 *
 * Without this, the last screen a user was on before closing the tab is never
 * recorded, which is exactly the drop-off the epic exists to measure.
 */
function attachLifecycleListeners(): void {
  if (listenersAttached || typeof window === 'undefined')
    return

  listenersAttached = true

  const flushIfHidden = () => {
    if (document.visibilityState === 'hidden')
      flushBehaviorEvents()
  }

  window.addEventListener('visibilitychange', flushIfHidden)
  window.addEventListener('pagehide', () => {
    flushBehaviorEvents()
  })
}

/**
 * Options for a screen view.
 *
 * `action` and `metadata` are `never` rather than absent, so passing either
 * is a type error at the call site instead of something the server rejects
 * later. There is nothing about rendering a screen that needs describing
 * beyond the screen itself and how long it was up.
 */
export interface ScreenViewOptions {
  action?: never
  metadata?: never

  /** A screen view belongs to no attempt; the server refuses one that claims to. */
  attemptId?: never

  /** Dwell time on the screen. */
  durationMs?: number
}

/**
 * Options for a funnel step.
 *
 * `action` is required — it is what a funnel is keyed by — and `metadata` is
 * narrowed to that action's allow-list, so the permitted keys and values
 * follow from the action rather than being a free-for-all.
 */
export interface ActionEventOptions<A extends BehaviorAction> {
  action: A

  /** Allow-listed literals only — see BehaviorMetadataByAction. */
  metadata?: BehaviorMetadataByAction[A]

  /**
   * The attempt this event belongs to, from newAttemptId(): one value for
   * every event of one attempt. Required, and enforced in CI by the
   * require-track-event-attempt-id lint rule, because leaving it off breaks
   * nothing visibly — the event is stored and counted, and its attempt
   * silently loses that step.
   */
  attemptId: string

  /** Elapsed time for a completed action. */
  durationMs?: number
}

export type TrackEventOptions<A extends BehaviorAction = BehaviorAction> =
  | ScreenViewOptions
  | ActionEventOptions<A>

/**
 * Record one behavior event.
 *
 * Fire-and-forget by design: it returns `void`, never throws, and never
 * awaits. A caller should not be able to tell whether instrumentation is
 * working, let alone be slowed down by it.
 */
export function trackEvent(
  eventName: typeof BehaviorEventName.ScreenViewed,
  screen: BehaviorScreen,
  options?: ScreenViewOptions,
): void
export function trackEvent<A extends BehaviorAction>(
  eventName: Exclude<BehaviorEventName, typeof BehaviorEventName.ScreenViewed>,
  screen: BehaviorScreen,
  options: ActionEventOptions<A>,
): void
export function trackEvent<A extends BehaviorAction>(
  eventName: BehaviorEventName,
  screen: BehaviorScreen,
  options: TrackEventOptions<A> = {},
): void {
  try {
    attachLifecycleListeners()

    buffer.push({
      payload: {
        client_event_id: uuid(),
        event_name: eventName,
        screen,
        action: options.action ?? null,
        session_id: currentBehaviorSessionId(),
        attempt_id: options.attemptId ?? null,
        occurred_at: timestamp(),
        duration_ms: options.durationMs ?? null,
        metadata: (options.metadata as Record<string, string> | undefined) ?? null,
      },
      providerUuid: providerFor(options.attemptId),
    })

    if (buffer.length > MAX_BUFFER)
      buffer = buffer.slice(buffer.length - MAX_BUFFER)

    if (buffer.length >= FLUSH_THRESHOLD)
      flushBehaviorEvents()
    else
      scheduleFlush()
  } catch {
    // A bug in instrumentation must not become a bug in the product.
  }
}

/**
 * Send what is buffered, then drop whatever could not go.
 *
 * Called on every auth transition. The server attributes `user_id` from the
 * session on the *ingest* request, not from the event, so an event buffered
 * by one user and flushed after someone else signs in is recorded against the
 * wrong person. At a shared front desk that is a routine sequence, not an
 * edge case.
 *
 * Draining first keeps the events that can still be sent under the identity
 * that produced them; clearing after is what makes the guarantee absolute,
 * because a failed or partial send must not leave anything behind to be
 * relabelled. Losing a few telemetry rows is the cheaper error.
 */
export function resetBehaviorBufferForAuthChange(): void {
  // Each call removes one provider's batch synchronously and sends it with
  // the identity currently in effect. The bound is belt-and-braces: MAX_BUFFER
  // over a minimum batch of one already terminates.
  let guard = 0

  while (buffer.length > 0 && guard < MAX_BUFFER) {
    guard += 1
    void flushBehaviorEvents()
  }

  buffer = []

  // Nothing from one identity carries into the next, the pins included.
  attemptProviders.clear()
}

/** Test seam: drop buffered events and cancel any pending flush. */
export function __resetBehaviorBufferForTests(): void {
  buffer = []
  attemptProviders.clear()
  if (flushTimer !== null) {
    clearTimeout(flushTimer)
    flushTimer = null
  }
  listenersAttached = false
}

/** Test seam: what is currently waiting to be sent. */
export function __behaviorBufferForTests(): readonly BehaviorEventPayload[] {
  return buffer.map(entry => entry.payload)
}
